import React, { useState, useEffect, useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, Minus, ShoppingBag, MessageCircle, AlertCircle, Ticket, X as XIcon, Stamp, ArrowRight, ScanBarcode } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { svcValidateGiftCard, svcUpsertAbandonedCart, svcMarkRecovered } from '@/services/entitiesService';
import { useSelectedStore, useProducts } from '@/store/dataStore';
import { useSettings } from '@/services/settingsService';
import { getCustomerToken, getStoredCustomer } from '@/services/customerService';
import { Link } from 'react-router-dom';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import { thumb } from '@/lib/img';

// ---- Add by barcode / internal code (inline mini-lookup) ----
// Lets the shopper type or scan a code and add the matching product in one
// keystroke — store staff can chain 20 items in seconds.
function AddByCode({ compact = false }) {
  const { products } = useProducts();
  const { addItem } = useCart();
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const submit = (e) => {
    e?.preventDefault?.();
    const raw = q.trim();
    if (!raw) return;
    // Match against both codes; case-insensitive on internal_code, exact on barcode.
    const lc = raw.toLowerCase();
    const found = products.find((p) =>
      (p.barcode || '').trim() === raw ||
      (p.internal_code || '').trim().toLowerCase() === lc
    );
    if (!found) { setErr(`No encontramos ningún producto con "${raw}"`); setFlash(''); return; }
    addItem(found, 1);
    setFlash(`✓ ${found.name} agregado`);
    setErr('');
    setQ('');
  };
  return (
    <form onSubmit={submit} className={`bg-yellow-50 border border-yellow-300 rounded-xl p-2.5 ${compact ? 'mt-4 w-full max-w-xs' : 'mb-2'}`} data-testid="cart-add-by-code">
      <label className="text-[10px] font-bold text-yellow-800 uppercase tracking-wider flex items-center gap-1">
        <ScanBarcode size={12} /> Agregar por código de barras o interno
      </label>
      <div className="flex gap-2 mt-1">
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setErr(''); setFlash(''); }}
          placeholder="Ej: 7591234567890 o BT-001"
          className="h-9 text-sm font-mono"
          data-testid="cart-add-by-code-input"
          autoComplete="off"
        />
        <Button type="submit" className="h-9 px-3 bg-[#1B2A4A] text-yellow-300 rounded-full" data-testid="cart-add-by-code-btn">
          <Plus size={14} />
        </Button>
      </div>
      {err   && <p className="text-[11px] text-red-600 mt-1" data-testid="cart-add-by-code-err">{err}</p>}
      {flash && <p className="text-[11px] text-green-700 mt-1" data-testid="cart-add-by-code-ok">{flash}</p>}
    </form>
  );
}

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, rawSubtotal, discount, giftCard, setGiftCard, checkoutWhatsApp, clearCart, wholesaleActive, wholesaleSavings, amountToReachWholesale, wholesaleThreshold, effectivePrice } = useCart();
  const [gcInput, setGcInput] = useState('');
  const [gcOpen, setGcOpen] = useState(false);
  const [gcErr, setGcErr] = useState('');
  const [gcBusy, setGcBusy] = useState(false);
  const applyGC = async () => {
    const code = gcInput.trim().toUpperCase();
    if (!code) return;
    setGcBusy(true); setGcErr('');
    try {
      const data = await svcValidateGiftCard(code);
      setGiftCard({ code: data.code, percent: data.percent });
      setGcInput('');
    } catch (e) { setGcErr(e.response?.data?.detail || 'Código inválido'); }
    finally { setGcBusy(false); }
  };
  const removeGC = () => { setGiftCard(null); setGcErr(''); };
  const { store } = useSelectedStore();
  const { settings } = useSettings();

  const [name, setName] = useState(() => getStoredCustomer()?.name || '');
  const [cedula, setCedula] = useState(() => getStoredCustomer()?.cedula || '');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [purchaseType, setPurchaseType] = useState('personal');
  const [errors, setErrors] = useState({});

  // Stable session id for abandoned-cart tracking (persisted in localStorage per browser)
  const sessionId = useMemo(() => {
    let sid = localStorage.getItem('bigtime_cart_session');
    if (!sid) { sid = 'c_' + Math.random().toString(36).slice(2, 12) + Date.now().toString(36); localStorage.setItem('bigtime_cart_session', sid); }
    return sid;
  }, []);

  // Debounced abandoned-cart save when the user has already typed name+phone
  useEffect(() => {
    if (!name || !phone || items.length === 0) return;
    const t = setTimeout(() => {
      svcUpsertAbandonedCart({
        customer: { name, cedula, phone, city, state: stateRegion, address, note },
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty })),
        subtotal: rawSubtotal,
        store_id: store?.id || null,
        session_id: sessionId,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [name, cedula, phone, city, stateRegion, address, note, items.length, store?.id]);

  // Autofill from cédula: when the shopper types a valid-looking cédula we
  // ping the public lookup and prefill remaining fields IF they're still
  // empty (never overwrite what they typed).
  const [lookupState, setLookupState] = useState({ status: 'idle' }); // idle | found | notfound
  useEffect(() => {
    const c = (cedula || '').trim();
    if (c.length < 5) { setLookupState({ status: 'idle' }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { api } = await import('@/services/authService');
        const { data } = await api.get(`/customers/lookup?cedula=${encodeURIComponent(c)}`);
        if (cancelled) return;
        setName((v) => v || data.name || '');
        setPhone((v) => v || data.phone || '');
        setCity((v) => v || data.city || '');
        setStateRegion((v) => v || data.state || '');
        setAddress((v) => v || data.address || '');
        setLookupState({ status: 'found', orders: data.orders_count });
      } catch {
        if (!cancelled) setLookupState({ status: 'notfound' });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cedula]);

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = 'Requerido';
    if (!cedula.trim()) e.cedula = 'Requerido';
    if (!phone.trim()) e.phone = 'Requerido';
    if (!city.trim()) e.city = 'Requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [payments, setPayments] = useState([]);                 // Optional: multi-tender payment breakdown
  const [checkoutError, setCheckoutError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleCheckout = async () => {
    if (!validate()) return;
    setCheckoutError('');
    setSubmitting(true);
    try {
      await checkoutWhatsApp(
        { name, cedula, phone, city, state: stateRegion, address, note, purchaseType },
        { payments: payments.length ? payments : null }
      );
      // Order persisted → mark abandoned-cart as recovered (fire-and-forget)
      svcMarkRecovered(sessionId).catch(() => {});
      clearCart();
      setIsOpen(false);
      // clear form after send
      setName(''); setCedula(''); setPhone(''); setCity(''); setStateRegion(''); setAddress(''); setNote('');
      setPayments([]);
    } catch (e) {
      setCheckoutError(e.response?.data?.detail || e.message || 'No pudimos registrar tu pedido. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" overlayClassName="z-[110]" className="z-[111] w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-5 border-b bg-[#1B2A4A] text-white">
          <SheetTitle className="text-white flex items-center gap-2">
            <ShoppingBag size={20} className="text-yellow-300" /> Tu Carrito
          </SheetTitle>
          {store && <p className="text-xs text-white/70">Pedido para: <b className="text-yellow-300">{store.name}</b></p>}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length > 0 && !getCustomerToken() && (
            <Link
              to="/mi-cuenta"
              onClick={() => setIsOpen(false)}
              data-testid="cart-loyalty-banner"
              className="block rounded-xl border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-yellow-100 p-3.5 hover:shadow-md hover:-translate-y-0.5 transition"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-[#1B2A4A] text-yellow-300 flex items-center justify-center flex-shrink-0">
                  <Stamp size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase tracking-widest text-yellow-700 font-bold">Programa de sellos</p>
                  <p className="text-sm font-bold text-[#1B2A4A] leading-tight">
                    Regístrate y gana premios con cada compra
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    1 compra = 1 sello · A los 10 sellos, Gift Card automática
                  </p>
                </div>
                <ArrowRight size={18} className="text-[#1B2A4A] flex-shrink-0" />
              </div>
            </Link>
          )}
          {items.length > 0 && getCustomerToken() && (
            <Link
              to="/mi-cuenta"
              onClick={() => setIsOpen(false)}
              data-testid="cart-loyalty-badge"
              className="block rounded-xl border border-emerald-300 bg-emerald-50 p-3 hover:bg-emerald-100 transition"
            >
              <div className="flex items-center gap-3">
                <Stamp className="text-emerald-700" size={20} />
                <div className="flex-1 text-sm">
                  <b className="text-emerald-800">Hola{getStoredCustomer()?.name ? `, ${getStoredCustomer().name.split(' ')[0]}` : ''}.</b>
                  <span className="text-slate-600"> Verás este pedido reflejado en tus sellos.</span>
                </div>
                <ArrowRight size={16} className="text-emerald-700" />
              </div>
            </Link>
          )}
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                <ShoppingBag size={32} className="text-yellow-600" />
              </div>
              <p className="text-slate-500 mb-1 font-medium">Tu carrito está vacío</p>
              <p className="text-xs text-slate-400">Agrega productos para hacer tu pedido</p>
              <AddByCode compact />
            </div>
          ) : (
            <>
              <AddByCode />
              {items.map((item) => {
                const key = item.__key || item.lineId || item.id;
                return (
                <div key={key} className="flex gap-3 bg-slate-50 rounded-lg p-2.5 border border-slate-100" data-testid={`cart-line-${key}`}>
                  <img src={thumb(item.image, 200)} alt={item.name} loading="lazy" decoding="async" className="w-16 h-16 object-cover rounded-md flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 line-clamp-2">{item.name}</p>
                    {(item.color?.name || item.size) && (
                      <div className="mt-0.5 flex flex-wrap gap-1" data-testid={`cart-line-variant-${key}`}>
                        {item.color?.name && (
                          <span className="inline-flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded-full px-1.5 py-0.5 text-slate-600">
                            {item.color.hex && <span className="w-2 h-2 rounded-full" style={{ background: item.color.hex }} />}
                            {item.color.name}
                          </span>
                        )}
                        {item.size && (
                          <span className="text-[10px] bg-white border border-slate-200 rounded-full px-1.5 py-0.5 text-slate-600">
                            Talla {item.size}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-sm font-bold text-[#1B2A4A] mt-0.5">
                      ${effectivePrice(item).toFixed(2)}
                      {wholesaleActive && item.wholesale_price != null && (
                        <>
                          <span className="text-[11px] text-slate-400 line-through ml-1">${(item.retail_price ?? item.price).toFixed(2)}</span>
                          <span className="text-[10px] font-bold text-green-700 ml-1">🎁 Mayorista</span>
                        </>
                      )}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex items-center border border-slate-300 rounded-full overflow-hidden bg-white">
                        <button onClick={() => updateQty(key, item.qty - 1)} className="px-2 py-1 hover:bg-slate-100"><Minus size={14} /></button>
                        <span className="px-3 text-sm font-semibold min-w-[28px] text-center">{item.qty}</span>
                        <button onClick={() => updateQty(key, item.qty + 1)} className="px-2 py-1 hover:bg-slate-100"><Plus size={14} /></button>
                      </div>
                      <button onClick={() => removeItem(key)} className="ml-auto text-red-500 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
                );
              })}

              <div className="pt-3 border-t space-y-2">
                <p className="text-sm font-bold text-[#1B2A4A] mb-1">Datos del cliente</p>
                {!store && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded flex items-center gap-2">
                    <AlertCircle size={14} /> Selecciona una tienda antes de continuar
                  </div>
                )}
                <div>
                  <Input data-testid="cart-name-input" placeholder="Nombre y apellido *" value={name} onChange={(e) => setName(e.target.value)} className={`h-10 ${errors.name ? 'border-red-400' : ''}`} />
                  {errors.name && <p className="text-[10px] text-red-500 mt-0.5">{errors.name}</p>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input placeholder="Cédula *" value={cedula} onChange={(e) => setCedula(e.target.value)} className={`h-10 ${errors.cedula ? 'border-red-400' : ''}`} data-testid="cart-cedula-input" />
                    {errors.cedula && <p className="text-[10px] text-red-500 mt-0.5">{errors.cedula}</p>}
                    {lookupState.status === 'found' && !errors.cedula && (
                      <p className="text-[10px] text-green-700 mt-0.5" data-testid="cart-cedula-autofill-ok">
                        ✓ Cliente recurrente ({lookupState.orders} pedido{lookupState.orders === 1 ? '' : 's'} previos) — datos autocompletados
                      </p>
                    )}
                  </div>
                  <div>
                    <Input data-testid="cart-phone-input" placeholder="Teléfono *" value={phone} onChange={(e) => setPhone(e.target.value)} className={`h-10 ${errors.phone ? 'border-red-400' : ''}`} />
                    {errors.phone && <p className="text-[10px] text-red-500 mt-0.5">{errors.phone}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input data-testid="cart-city-input" placeholder="Ciudad *" value={city} onChange={(e) => setCity(e.target.value)} className={`h-10 ${errors.city ? 'border-red-400' : ''}`} />
                    {errors.city && <p className="text-[10px] text-red-500 mt-0.5">{errors.city}</p>}
                  </div>
                  <Input placeholder="Estado" value={stateRegion} onChange={(e) => setStateRegion(e.target.value)} className="h-10" />
                </div>
                <Input placeholder="Dirección (opcional)" value={address} onChange={(e) => setAddress(e.target.value)} className="h-10" />
                <Input placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-10" />

                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo de compra *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: 'emprendedor', label: 'Emprendedor', ico: '💼' },
                      { v: 'regalo', label: 'Regalo', ico: '🎁' },
                      { v: 'personal', label: 'Uso Personal', ico: '👤' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setPurchaseType(opt.v)}
                        className={`text-xs py-2 px-1 rounded-lg border-2 font-semibold transition ${purchaseType === opt.v ? 'bg-yellow-50 border-yellow-400 text-[#1B2A4A]' : 'bg-white border-slate-200 text-slate-500 hover:border-yellow-200'}`}
                      >
                        <div className="text-lg mb-0.5">{opt.ico}</div>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <PaymentBreakdown
                  payments={payments}
                  onChange={setPayments}
                  totalUsd={subtotal}
                />
              </div>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t bg-white p-4">
            <>
                {/* Gift Card */}
                <div className={`mb-3 rounded-lg ${giftCard || gcOpen ? 'bg-yellow-50 border border-yellow-200 p-2' : ''}`} data-testid="giftcard-box">
                  {!giftCard && !gcOpen ? (
                    <button type="button" onClick={() => setGcOpen(true)} data-testid="giftcard-toggle" className="flex items-center gap-2 text-xs font-bold text-yellow-800 hover:underline py-1">
                      <Ticket size={14} /> ¿Tienes una Gift Card?
                    </button>
                  ) : !giftCard ? (
                    <>
                      <div className="flex items-center gap-2 text-xs font-bold text-yellow-800 mb-1"><Ticket size={14} /> ¿Tienes una Gift Card?</div>
                      <div className="flex gap-1">
                        <Input value={gcInput} onChange={(e) => setGcInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && applyGC()} placeholder="GC-XXXXXXXX" data-testid="giftcard-input" className="h-9 text-sm bg-white" />
                        <Button onClick={applyGC} disabled={gcBusy || !gcInput} data-testid="btn-apply-giftcard" size="sm" className="h-9 bg-[#1B2A4A] text-yellow-300 rounded">
                          {gcBusy ? '...' : 'Aplicar'}
                        </Button>
                      </div>
                      {gcErr && <div className="text-[11px] text-red-600 mt-1" data-testid="giftcard-error">{gcErr}</div>}
                    </>
                  ) : (
                    <div className="flex items-center justify-between" data-testid="giftcard-applied">
                      <div>
                        <div className="text-xs font-bold text-yellow-800 flex items-center gap-1"><Ticket size={14} /> {giftCard.code}</div>
                        <div className="text-[11px] text-green-700">Descuento {giftCard.percent}% (-${discount.toFixed(2)})</div>
                      </div>
                      <button onClick={removeGC} className="text-slate-500 hover:text-red-600 p-1" data-testid="btn-remove-giftcard"><XIcon size={16} /></button>
                    </div>
                  )}
                </div>
                {/* Wholesale tier banner */}
                {wholesaleActive ? (
                  <div className="mb-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-xl p-3 text-sm shadow-sm" data-testid="wholesale-banner-active">
                    <div className="flex items-center gap-2 font-bold">
                      <span className="text-lg">🎁</span> ¡Descuento Mayorista aplicado!
                    </div>
                    <div className="text-[11px] opacity-95 mt-0.5">
                      Ahorras <b>${wholesaleSavings.toFixed(2)}</b> por comprar ≥ ${wholesaleThreshold}.
                    </div>
                  </div>
                ) : amountToReachWholesale > 0 && amountToReachWholesale < 150 ? (
                  <div className="mb-3 bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-sm" data-testid="wholesale-banner-hint">
                    <div className="flex items-center gap-2 font-bold text-yellow-900">
                      <span>💡</span> Estás a <b>${amountToReachWholesale.toFixed(2)}</b> del <b>precio mayorista</b>.
                    </div>
                    <div className="text-[11px] text-yellow-800 mt-0.5">
                      Añade más productos y todos bajarán a su Precio Mayorista automáticamente.
                    </div>
                  </div>
                ) : null}
                {/* Dual pricing */}
                {(() => {
                  const rate = Number(settings?.bs_rate || 0);
                  const disc = Number(settings?.usd_discount_percent || 0);
                  const usdDiscounted = subtotal * (1 - disc / 100);
                  const bsTotal = subtotal * rate;
                  return (
                    <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                      <div className="bg-slate-50 rounded-lg p-2">
                        <div className="text-[11px] text-slate-500 uppercase tracking-wider">Total en Bs</div>
                        <div className="text-lg font-extrabold text-[#1B2A4A]">Bs {bsTotal.toFixed(2)}</div>
                        <div className="text-[10px] text-slate-500">Tasa: {rate.toFixed(2)}</div>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                        <div className="text-[11px] text-green-700 uppercase tracking-wider font-bold">Divisas {disc > 0 ? `(-${disc}%)` : ''}</div>
                        <div className="text-lg font-extrabold text-green-700">${usdDiscounted.toFixed(2)}</div>
                        {disc > 0 && <div className="text-[10px] text-slate-500 line-through">${subtotal.toFixed(2)}</div>}
                      </div>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-slate-600 font-medium">Total (USD lista)</span>
                  <div className="text-right">
                    {giftCard && discount > 0 && (
                      <div className="text-[11px] text-slate-400 line-through">${rawSubtotal.toFixed(2)}</div>
                    )}
                    <span className="text-xl font-extrabold text-[#1B2A4A]" data-testid="cart-total">${subtotal.toFixed(2)}</span>
                  </div>
                </div>
                <Button onClick={handleCheckout} disabled={!store || submitting} data-testid="cart-checkout-btn" className="w-full bg-[#25D366] hover:bg-[#1FBA57] text-white font-bold h-12 rounded-full disabled:opacity-50">
                  <MessageCircle size={18} className="mr-2" /> {submitting ? 'Enviando...' : 'Enviar pedido por WhatsApp'}
                </Button>
                {checkoutError && (
                  <div data-testid="cart-checkout-error" className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{checkoutError}</span>
                  </div>
                )}
                <button onClick={clearCart} className="w-full text-xs text-slate-500 hover:text-red-500 py-1 mt-1">Vaciar carrito</button>
            </>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
